import type { Project, Task, TaskActivity } from '@portfolio-pm/shared';
import type { KeyboardEvent } from 'react';

/**
 * Shared helpers for the task detail inspector: date maths, summary
 * aggregation, and small formatting/colour utilities. Extracted from
 * TaskDetailPanel.tsx.
 */

export function startOfDay(value: number): number {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

export function addDays(value: number, days: number): number {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date.getTime();
}

export function earliestTaskAnchor(task: Task): number | undefined {
  if (task.dueAt === undefined) return task.scheduledAt;
  if (task.scheduledAt === undefined) return task.dueAt;
  return Math.min(task.dueAt, task.scheduledAt);
}

export function summarizeByProject(
  tasks: Task[],
  projectsById: Map<string, Project>,
): Array<{ projectId: string; label: string; count: number; percent: number }> {
  const total = Math.max(1, tasks.length);
  const counts = new Map<string, number>();
  for (const task of tasks) counts.set(task.projectId, (counts.get(task.projectId) ?? 0) + 1);
  return [...counts.entries()]
    .map(([projectId, count]) => ({
      projectId,
      label: projectsById.get(projectId)?.name ?? 'Project',
      count,
      percent: Math.round((count / total) * 100),
    }))
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));
}

export function summarizeTimeline(
  tasks: Task[],
  today: number,
): Array<{ label: string; count: number; effort: number; percent: number }> {
  const days = Array.from({ length: 7 }, (_, index) => {
    const start = addDays(today, index);
    return {
      start,
      end: addDays(start, 1),
      label: index === 0 ? 'Today' : formatShortWeekday(start),
      count: 0,
      effort: 0,
      percent: 0,
    };
  });

  for (const task of tasks) {
    const anchor = earliestTaskAnchor(task);
    if (anchor === undefined) continue;
    const day = days.find((entry) => anchor >= entry.start && anchor < entry.end);
    if (!day) continue;
    day.count += 1;
    day.effort += task.estimateMinutes ?? 0;
  }

  const maxVolume = Math.max(1, ...days.map((entry) => entry.count + entry.effort / 60));
  return days.map((entry) => ({
    ...entry,
    percent: Math.round(((entry.count + entry.effort / 60) / maxVolume) * 100),
  }));
}

export function summarizeCompletions(
  allTasks: Task[],
  taskActivities: TaskActivity[],
  today: number,
): Array<{ label: string; count: number; percent: number }> {
  const days = Array.from({ length: 7 }, (_, index) => {
    const start = addDays(today, index - 6);
    return {
      start,
      end: addDays(start, 1),
      label: index === 6 ? 'Today' : formatShortWeekday(start),
      count: 0,
      percent: 0,
    };
  });

  const completionTimes = [
    ...allTasks.flatMap((task) => (task.completedAt === undefined ? [] : [task.completedAt])),
    ...taskActivities
      .filter((activity) => activity.type === 'completed')
      .map((activity) => activity.createdAt),
  ];

  for (const completedAt of completionTimes) {
    const day = days.find((entry) => completedAt >= entry.start && completedAt < entry.end);
    if (day) day.count += 1;
  }

  const maxCount = Math.max(1, ...days.map((entry) => entry.count));
  return days.map((entry) => ({
    ...entry,
    percent: Math.round((entry.count / maxCount) * 100),
  }));
}

export function formatMinutes(value: number): string {
  if (value <= 0) return '0 min';
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
}

export function formatCompactMinutes(value: number): string {
  if (value <= 0) return '0min';
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  if (hours === 0) return `${minutes}min`;
  if (minutes === 0) return `${hours}hr${hours === 1 ? '' : 's'}`;
  return `${hours}hr${hours === 1 ? '' : 's'} ${minutes}min`;
}

const WEEKDAY_SHORT_FORMAT = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const MONTH_DAY_FORMAT = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

export function formatShortWeekday(value: number): string {
  return WEEKDAY_SHORT_FORMAT.format(new Date(value));
}

export function toDateStr(v: number | undefined): string {
  if (v === undefined) return '';
  const d = new Date(v);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function toTimeStr(v: number | undefined): string {
  if (v === undefined) return '00:00';
  const d = new Date(v);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function getRepeatLabel(task: Task): string {
  if (!task.recurrence) return 'Never';
  const parts = parseRepeatRuleParts(task.recurrence.rrule);
  if (!parts) return 'Repeats configured';
  if (!task.recurrence.fromCompletion && parts.byday === 'MO,TU,WE,TH,FR') {
    return 'Repeats every weekday';
  }

  const unit = unitForFrequency(parts.freq);
  if (!unit) return 'Repeats configured';
  const cadence = task.recurrence.fromCompletion ? 'after' : 'every';
  const unitText = parts.interval === 1 ? unit : `${unit}s`;
  const text =
    parts.interval === 1 ? `${cadence} ${unitText}` : `${cadence} ${parts.interval} ${unitText}`;
  return `Repeats ${text}`;
}

function parseRepeatRuleParts(rrule: string):
  | {
      freq: string;
      interval: number;
      byday?: string;
    }
  | undefined {
  const parts = new Map<string, string>();
  for (const segment of rrule.split(';')) {
    const [key, value] = segment.split('=');
    if (key && value) parts.set(key.toUpperCase(), value.toUpperCase());
  }
  const freq = parts.get('FREQ');
  if (!freq) return undefined;
  const interval = Number.parseInt(parts.get('INTERVAL') ?? '1', 10);
  if (!Number.isInteger(interval) || interval <= 0) return undefined;
  const byday = parts.get('BYDAY');
  return byday ? { freq, interval, byday } : { freq, interval };
}

function unitForFrequency(freq: string): string | undefined {
  switch (freq) {
    case 'DAILY':
      return 'day';
    case 'WEEKLY':
      return 'week';
    case 'MONTHLY':
      return 'month';
    case 'YEARLY':
      return 'year';
    default:
      return undefined;
  }
}

export function getPriorityAccent(priority: Task['priority']): string {
  switch (priority) {
    case 1:
      return '#f97316';
    case 2:
      return '#d97706';
    case 3:
      return '#276176';
    default:
      return 'var(--app-border-strong)';
  }
}

export function formatTaskAnchorLabel(task: Task): string {
  const anchor = task.dueAt ?? task.scheduledAt;
  if (anchor === undefined) return '';
  const today = startOfDay(Date.now());
  const anchorDay = startOfDay(anchor);
  if (anchorDay === today) return 'Today';
  if (anchorDay === addDays(today, 1)) return 'Tomorrow';
  return MONTH_DAY_FORMAT.format(new Date(anchor));
}

export function getTagColorForDetail(tag: string): string {
  const palette = ['#6a9f33', '#d97706', '#276176', '#a64237', '#707b35'];
  const hash = [...tag].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return palette[hash % palette.length] ?? '#6a9f33';
}

export function parseTags(value: string): string[] {
  return value
    .split(',')
    .map((tag) => tag.trim().replace(/^#/, ''))
    .filter(Boolean);
}

export function commitOnEnter(event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void {
  if (event.key === 'Enter') {
    event.preventDefault();
    event.currentTarget.blur();
  }
}
